import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Calculator, Check, FileText, Mail, PackageCheck, Trash2, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { fmtDateTime, fmtEuro } from "@/lib/format";
import { Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const PO_STATUS = { DRAFT: ["Bozza", "bg-amber-100 text-amber-900"], CONFIRMED: ["Confermato", "bg-sky-100 text-sky-900"], PARTIAL: ["Ricevuto in parte", "bg-violet-100 text-violet-900"],
  RECEIVED: ["Ricevuto", "bg-emerald-100 text-emerald-900"], CANCELLED: ["Annullato", "bg-zinc-100 text-zinc-600"] };

export const EMAIL_STATUS = { sending: ["Email in invio", "bg-sky-50 text-sky-900"], sent: ["Email inviata", "bg-emerald-50 text-emerald-800"],
  failed: ["Email non inviata", "bg-red-100 text-red-800"], no_email: ["Fornitore senza email", "bg-amber-100 text-amber-900"] };

export const Badge = ({ map, value, testId }) => {
  const [label, cls] = map[value] || [value, ""];
  return <span className={`px-1.5 py-0.5 text-[11px] font-semibold uppercase ${cls}`} data-testid={testId}>{label}</span>;
};

export const RunNow = ({ onDone }) => {
  const { can } = useAuth();
  const [last, setLast] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadLast = useCallback(() => api.get("/warehouse/replenish/last").then((r) => setLast(r.data)).catch(() => {}), []);
  useEffect(() => { loadLast(); }, [loadLast]);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/warehouse/replenish/run");
      toast.success(`Calcolo completato: ${r.data.po_created} nuove proposte d'ordine, ${r.data.refills} reintegri furgone`);
      loadLast();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-muted-foreground" data-testid="replenish-last-run">Calcolo automatico ogni giorno alle 07:00{last && ` · ultimo: ${fmtDateTime(last.at)} (${last.by})`}</span>
      {can("inventory:write") && <Button onClick={run} disabled={busy} data-testid="replenish-run-button"><Calculator className="mr-2 h-4 w-4" />{busy ? "Calcolo…" : "Calcola ora"}</Button>}
    </div>
  );
};

const ReceiveDialog = ({ po, onClose, onDone }) => {
  const [rows, setRows] = useState({});
  useEffect(() => {
    if (po) setRows(Object.fromEntries(po.lines.map((l) => [l.item_id, { qty: Math.max(l.qty - l.received_qty, 0), serials: "" }])));
  }, [po]);
  if (!po) return null;
  const set = (id, k, v) => setRows({ ...rows, [id]: { ...rows[id], [k]: v } });
  const save = async () => {
    try {
      const lines = po.lines.map((l) => ({ item_id: l.item_id, qty: l.serialized ? 0 : Number(rows[l.item_id]?.qty) || 0,
        serials: l.serialized ? (rows[l.item_id]?.serials || "").split(/[\s,;]+/).filter(Boolean) : [] }));
      const r = await api.post(`/warehouse/purchase-orders/${po.id}/receive`, { lines });
      toast.success(r.data.status === "RECEIVED" ? "Ordine ricevuto completamente" : "Ricevimento parziale registrato");
      onDone();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="po-receive-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Ricevimento {po.number}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Carico in {po.warehouse_name}. Per gli articoli a seriale inserisci i numeri di serie ricevuti, uno per riga.</p>
        <div className="space-y-3">
          {po.lines.map((l) => (
            <div key={l.item_id} className="border p-3" data-testid={`po-receive-line-${l.code}`}>
              <div className="flex justify-between text-sm"><span><span className="font-mono text-xs font-semibold">{l.code}</span> {l.name}</span><span className="font-mono text-xs">ordinati {l.qty} · ricevuti {l.received_qty}</span></div>
              {l.serialized
                ? <Textarea className="mt-2 min-h-20 font-mono" placeholder="Seriali ricevuti" value={rows[l.item_id]?.serials || ""} onChange={(e) => set(l.item_id, "serials", e.target.value)} data-testid={`po-receive-serials-${l.code}`} />
                : <Input className="mt-2 w-40" type="number" min="0" step="any" value={rows[l.item_id]?.qty ?? 0} onChange={(e) => set(l.item_id, "qty", e.target.value)} data-testid={`po-receive-qty-${l.code}`} />}
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} data-testid="po-receive-confirm"><PackageCheck className="mr-2 h-4 w-4" />Registra carico</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DraftLines = ({ po, lines, setLines }) => (
  <div className="space-y-1.5">
    {lines.map((l, i) => (
      <div key={l.item_id} className="grid grid-cols-[1fr_110px_90px_36px] items-center gap-2 text-sm">
        <span><span className="font-mono text-xs font-semibold">{l.code}</span> {l.name}</span>
        <Input type="number" min="0" step="any" className="h-8" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} data-testid={`po-line-qty-${po.number}-${l.code}`} />
        <span className="text-right font-mono text-xs">{fmtEuro((Number(l.qty) || 0) * l.unit_cost)}</span>
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setLines(lines.filter((_, j) => j !== i))} aria-label="Rimuovi riga" data-testid={`po-line-remove-${po.number}-${l.code}`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    ))}
  </div>
);

const PoCard = ({ po, suppliers, onChanged, onReceive }) => {
  const { can } = useAuth();
  const [lines, setLines] = useState(po.lines);
  const [supplier, setSupplier] = useState(po.supplier_id || "");
  useEffect(() => { setLines(po.lines); setSupplier(po.supplier_id || ""); }, [po]);
  const write = can("inventory:write");
  const draft = po.status === "DRAFT" && write;
  const call = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const save = () => api.put(`/warehouse/purchase-orders/${po.id}`, { lines: lines.map((l) => ({ item_id: l.item_id, qty: Number(l.qty) })), supplier_id: supplier || null, notes: po.notes || "" });
  const total = lines.reduce((s, l) => s + (Number(l.qty) || 0) * l.unit_cost, 0);
  const openPdf = async () => {
    try {
      const r = await api.get(`/warehouse/purchase-orders/${po.id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="border bg-white p-4" data-testid={`po-card-${po.number}`}>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <span className="font-mono text-sm font-bold">{po.number}</span>
        <Badge map={PO_STATUS} value={po.status} testId={`po-status-${po.number}`} />
        <span className="text-sm">{po.warehouse_name}</span>
        {po.source === "AUTO" && <span className="text-[11px] uppercase tracking-wider text-muted-foreground">proposta automatica</span>}
        {po.email_status && <Badge map={EMAIL_STATUS} value={po.email_status} testId={`po-email-${po.number}`} />}
        <span className="ml-auto font-mono text-sm font-semibold">{fmtEuro(total)}</span>
      </div>
      <div className="mb-3 flex items-center gap-2 text-sm">
        <span className="micro-label">Fornitore</span>
        {draft ? (
          <Select value={supplier || undefined} onValueChange={setSupplier}>
            <SelectTrigger className="h-8 w-72" data-testid={`po-supplier-${po.number}`}><SelectValue placeholder="Fornitore da definire" /></SelectTrigger>
            <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        ) : <span className="font-semibold">{suppliers.find((s) => s.id === po.supplier_id)?.name || "—"}</span>}
      </div>
      {draft ? <DraftLines po={po} lines={lines} setLines={setLines} /> : (
        <div className="space-y-1 text-sm">
          {po.lines.map((l) => <div key={l.item_id} className="flex justify-between"><span><span className="font-mono text-xs font-semibold">{l.code}</span> {l.name}</span><span className="font-mono text-xs">{l.received_qty}/{l.qty} {l.unit}</span></div>)}
        </div>
      )}
      {write && (
        <div className="mt-4 flex flex-wrap gap-2 border-t pt-3">
          {draft && <Button size="sm" variant="outline" onClick={() => call(save, "Bozza salvata")} disabled={!lines.length} data-testid={`po-save-${po.number}`}>Salva modifiche</Button>}
          {draft && <Button size="sm" onClick={() => call(async () => { await save(); const r = await api.post(`/warehouse/purchase-orders/${po.id}/confirm`); if (r.data.email_status === "no_email") toast.warning("Il fornitore non ha un'email in anagrafica: scarica il PDF e invialo a mano"); }, "Ordine confermato")} disabled={!lines.length} data-testid={`po-confirm-${po.number}`}><Check className="mr-1 h-3.5 w-3.5" />Conferma e invia</Button>}
          {po.status !== "DRAFT" && <Button size="sm" variant="outline" onClick={openPdf} data-testid={`po-pdf-${po.number}`}><FileText className="mr-1 h-3.5 w-3.5" />PDF</Button>}
          {["CONFIRMED", "PARTIAL"].includes(po.status) && <Button size="sm" variant="outline" onClick={() => call(async () => { const r = await api.post(`/warehouse/purchase-orders/${po.id}/send-email`); if (r.data.email_status === "no_email") throw Object.assign(new Error(), { response: { data: { detail: "Il fornitore non ha un'email in anagrafica: aggiungila in Anagrafiche o scarica il PDF" } } }); }, "Invio email in corso")} data-testid={`po-resend-${po.number}`}><Mail className="mr-1 h-3.5 w-3.5" />Reinvia email</Button>}
          {["CONFIRMED", "PARTIAL"].includes(po.status) && <Button size="sm" onClick={() => onReceive(po)} data-testid={`po-receive-${po.number}`}><PackageCheck className="mr-1 h-3.5 w-3.5" />Registra ricevimento</Button>}
          {["DRAFT", "CONFIRMED"].includes(po.status) && <Button size="sm" variant="ghost" onClick={() => call(() => api.post(`/warehouse/purchase-orders/${po.id}/cancel`), "Ordine annullato")} data-testid={`po-cancel-${po.number}`}><X className="mr-1 h-3.5 w-3.5" />Annulla</Button>}
        </div>
      )}
    </div>
  );
};

export const PurchaseOrdersTab = ({ onChanged }) => {
  const { parties, inCompany } = useData();
  const [status, setStatus] = useState("DRAFT,CONFIRMED,PARTIAL");
  const [raw, setRows] = useState([]);
  const rows = raw.filter((r) => inCompany(r.company_id));
  const [receiving, setReceiving] = useState(null);
  const load = useCallback(() => api.get("/warehouse/purchase-orders", { params: { status: status === "all" ? undefined : status } }).then((r) => setRows(r.data)).catch(() => {}), [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!rows.some((p) => p.email_status === "sending")) return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [rows, load]);
  const suppliers = parties.filter((p) => p.roles.includes("supplier"));
  const changed = () => { load(); onChanged(); };
  return (
    <div data-testid="purchase-orders-tab">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-56 bg-white" data-testid="po-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="DRAFT,CONFIRMED,PARTIAL">Aperti</SelectItem><SelectItem value="DRAFT">Bozze da rivedere</SelectItem><SelectItem value="CONFIRMED,PARTIAL">In attesa di consegna</SelectItem>
            <SelectItem value="RECEIVED">Ricevuti</SelectItem><SelectItem value="CANCELLED">Annullati</SelectItem><SelectItem value="all">Tutti</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto"><RunNow onDone={changed} /></div>
      </div>
      {rows.length === 0 ? <Empty text="Nessun ordine fornitore" testId="po-empty" /> : <div className="grid gap-4 xl:grid-cols-2">{rows.map((po) => <PoCard key={po.id} po={po} suppliers={suppliers} onChanged={changed} onReceive={setReceiving} />)}</div>}
      <ReceiveDialog po={receiving} onClose={() => setReceiving(null)} onDone={changed} />
    </div>
  );
};
